import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
import { styled } from "@mui/system";

import { CapexPlanResult, CapexQueryModel } from "../Data/CapexPlan";
import { _getLocations } from "middlewares/LocationApi/location";

import { _getCapexPlanData } from "middlewares/CapexPlanApi/CapexPlan";
import Spinner from "components/Spinner";

import { useAppState } from "context/appState.context";

import CapexValueRow from "./CapexValueRow";
import CapexLocationNameRow from "./CapexLocationNameRow";
import CapexSelectScenarioRow from "./CapexSelectScenarioRow";
import FilterPanel from "../FilterPanel";

const StyledTableContainer = styled(TableContainer)({
  width: '100%',
  maxWidth: "100%",
  overflowX: "auto",
  boxSizing: 'border-box',
});

const CapexPlan: React.FC = () => {
  const { userRole } = useAppState();
  const [loading, setLoading] = useState(true);

  const [locationOptions, setLocationOptions] = useState<
    { id: number; label: string; value: string }[]
  >([]);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([
    -1,
  ]);
  const [queryModel, setQueryModel] = useState<CapexQueryModel[]>([]);
  const [capexPlanResult, setCapexPlanResult] = useState<CapexPlanResult[]>([]);
  const years = useMemo(() => {
    const minYear = 2020;
    const maxYear = 2050;
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  }, [capexPlanResult]);

  useEffect(() => {
    if (userRole !== null && userRole !== "") {
      getEssentialLocationDetails();
    }
  }, []);

  /**
   * Fetches location names from the API (using user role) and updates the state.
   * Sets loading state to true while fetching data and false after completion.
   */
  const getEssentialLocationDetails = async () => {
    try {
      const res = await _getLocations();
      if (res) {
        let tempObject: Array<CapexQueryModel> = [];
        const formattedOptions = res.map((loc) => ({
          id: loc.locationId,
          label: loc.locationName,
          value: loc.locationId.toString(),
        }));
        const selectAllOption = {
          id: 0,
          label: "Select All",
          value: "0",
        };
        res.forEach((element) => {
          let obj = { LocationId: element.locationId, Scenario: "", ToCompute: true };
          tempObject.push(obj);
        });
        setQueryModel(tempObject);
        setLocationOptions([selectAllOption, ...formattedOptions]);
      }
    } catch (err) {
      console.error("CapexPlan getLocationNames Error:", err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches the Capex Plan List from the backend and updates the state with the retrieved data.
   *
   * @async
   * @function getCapexPlanList
   * @returns {Promise<void>}
   */
  const getCapexPlanList = async () => {
    try {
      setLoading(true);
      const res = await _getCapexPlanData(queryModel);
      if (res) {
        setCapexPlanResult(res);
      }
    } catch (err) {
      console.error("CapexPlan getCapexPlanList Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      queryModel !== null &&
      queryModel !== undefined &&
      queryModel.length > 0
    ) {
      getCapexPlanList();
    }
  }, [queryModel]);

  const handleScenarioChange = (
    value: string | null | number,
    locationId: number | null
  ) => {
    const updatedScenarioName = value;
    let tempQueryModel = queryModel;
    const itemIndex = tempQueryModel.findIndex((i) => i.LocationId === locationId);
    if (itemIndex > -1) {
      tempQueryModel[itemIndex].Scenario =
        updatedScenarioName != null ? updatedScenarioName.toString() : "";
    }
    setQueryModel(tempQueryModel);
    if (
      queryModel !== null &&
      queryModel !== undefined &&
      queryModel.length > 0
    ) {
      getCapexPlanList();
    }
  };

  const handleLocationChange = (selectedOptions: number[]) => {
    setSelectedLocations(selectedOptions);
  };

  useEffect(() => {
    let tempQueryModel = queryModel;
    tempQueryModel.forEach((element) => {
      if (selectedLocations.indexOf(element.LocationId) > -1) {
        element.ToCompute = true;
      } else if (selectedLocations.indexOf(element.LocationId) === -1) {
        element.ToCompute = false;
      }
    });
    setQueryModel(tempQueryModel);
  }, [selectedLocations]);

  const handleSearch = async () => {
    if (
      queryModel !== null &&
      queryModel !== undefined &&
      queryModel.length > 0
    ) {
      getCapexPlanList();
    }
  };

  return (
    <Box sx={{ width: 'calc(100vw - 320px)', maxWidth: 'calc(100vw - 320px)', overflow: 'visible', position: 'relative' }}>
      <Box sx={{ minHeight: '100vh' }}>
        <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>
          {loading ? (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ height: "100vh" }}
            >
              <Spinner size={80} data-testid="spinner" />
            </Grid>
          ) : (
            <>
              <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>
                <Grid item xs={12} sx={{ mr: 5 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: "bold" }}
                    data-testid="portfolioView"
                  >
                    Portfolio View
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold" }}
                    data-testid="capexLabel"
                  >
                    CapEx Plan
                  </Typography>
                </Grid>
              </Grid>
              <br />
              <Divider />
              <br />

              {/* Filter Starts */}

              <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%', ml: 0, pl: 0, pr: 0 }}>
                <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%' }}>
                  <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%' }}>
                    <FilterPanel
                      locationOptions={locationOptions}
                      selectedLocations={selectedLocations}
                      onLocationChange={handleLocationChange}
                      onSearch={handleSearch}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Filter Ends */}

              {queryModel.length === 0 ? (
                <Grid item xs={11} style={{ textAlign: "center" }}>
                  <Typography
                    variant="h6"
                    color="gray"
                    style={{ fontWeight: "bold" }}
                    sx={{ mt: 15, mb: 20, ml: 20 }}
                  >
                    No Capex Data
                  </Typography>
                </Grid>
              ) : (
                <Grid container spacing={2} sx={{ width: 'calc(100% / 0.75)', maxWidth: 'calc(100% / 0.75)', ml: 0, pl: 0, pr: 0, transform: 'scale(0.75)', transformOrigin: 'top left', boxSizing: 'border-box' }}>
                  <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%' }}>
                    <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%' }}>
                      <StyledTableContainer>
                        <Table>
                          <TableBody>
                            {/* Location Name Table Row */}

                            <CapexLocationNameRow capexPlanResult={capexPlanResult} />

                            {/* Scenario Table Row */}

                            <CapexSelectScenarioRow
                              capexPlanResult={capexPlanResult}
                              onChange={handleScenarioChange}
                            />

                            {/* Years Table Row */}

                            <CapexValueRow
                              capexPlanResult={capexPlanResult}
                              years={years}
                            />
                          </TableBody>
                        </Table>
                      </StyledTableContainer>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default CapexPlan;
